import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import AskScratch from "@/components/AskScratch";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard, CalendarClock, Timer, DollarSign, Users,
  UtensilsCrossed, ShoppingBag, Sparkles, Settings as SettingsIcon,
  Flag, LogOut, Sparkle,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/tee-sheet", label: "Tee Sheet", icon: CalendarClock },
  { to: "/pace", label: "Pace", icon: Timer },
  { to: "/revenue", label: "Revenue", icon: DollarSign },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/fnb", label: "F&B", icon: UtensilsCrossed },
  { to: "/pro-shop", label: "Pro Shop", icon: ShoppingBag },
  { to: "/insights", label: "Insights", icon: Sparkles },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export default function Layout() {
  const { user, course, logout } = useApp();
  const nav = useNavigate();
  const [askOpen, setAskOpen] = useState(false);

  const doLogout = () => { logout(); nav("/login"); };

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });

  return (
    <div className="min-h-screen flex bg-[#F7F9F8]">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 sidebar-grain text-white flex flex-col fixed h-screen z-30">
        <div className="px-6 h-16 flex items-center gap-2.5 border-b border-white/8">
          <div className="w-8 h-8 rounded-lg bg-white/10 border border-white/12 flex items-center justify-center">
            <Flag size={16} className="text-white" />
          </div>
          <span className="font-display text-lg font-semibold tracking-tight">ScratchHQ</span>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-0.5 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-${to === "/" ? "overview" : to.replace(/^\//, "")}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isActive
                    ? "bg-white/10 text-white"
                    : "text-white/55 hover:text-white hover:bg-white/5"
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon size={17} className={isActive ? "text-[#7FB89B]" : ""} />
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/8">
          <button
            data-testid="ask-scratch-sidebar"
            onClick={() => setAskOpen(true)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/[0.07] hover:bg-white/[0.12] transition-colors text-sm font-medium text-white mb-2"
          >
            <Sparkle size={16} className="text-[#C7A24A]" />
            Ask Scratch
          </button>
          <div className="flex items-center gap-3 px-2 py-2">
            <img src={user?.avatar} alt={user?.name} className="w-8 h-8 rounded-full object-cover" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.full_name || user?.name}</div>
              <div className="text-xs text-white/45 truncate">{user?.role}</div>
            </div>
            <button data-testid="logout-btn" onClick={doLogout} className="text-white/45 hover:text-white transition-colors">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64 min-w-0">
        <header className="sticky top-0 z-20 h-16 px-8 flex items-center justify-between backdrop-blur-md bg-[#F7F9F8]/85 border-b border-[#E5E7EB]">
          <div>
            <div className="font-display text-base font-semibold tracking-tight text-[#1C1F1E]">
              {course?.name}
            </div>
            <div className="text-xs text-[#52525B]">{today}</div>
          </div>
          <button
            data-testid="ask-scratch-trigger"
            onClick={() => setAskOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#0F3F2D] hover:bg-[#165c42] text-white text-sm font-medium transition-colors active:scale-95"
          >
            <Sparkle size={15} className="text-[#C7A24A]" />
            Ask Scratch
          </button>
        </header>

        <main className="p-8 max-w-[1600px]">
          <Outlet />
        </main>
      </div>

      <AskScratch open={askOpen} onClose={() => setAskOpen(false)} />
    </div>
  );
}
